<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';

import AgentLayout from 'dashboard/components-next/aiAgents/agent/AgentLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { useBrain } from './useBrain';

// Shared tool catalog (D12). Edits run the 409 dependent-agents confirm flow:
// the first save without change_confirmed returns the affected agents; the
// operator confirms the blast radius and we re-submit with
// changeConfirmed: true.
const { t } = useI18n();
const store = useStore();
const { isAdmin } = useAdmin();
const { brain } = useBrain();

const tools = useMapGetter('aiAgentTools/getRecords');
const uiFlags = useMapGetter('aiAgentTools/getUIFlags');

const editDialogRef = ref(null);
const dependentsDialogRef = ref(null);
const editedTool = ref(null);
const editedDescription = ref('');
const editedReadOnly = ref(false);
const dependentAgents = ref([]);

const sortedTools = computed(() =>
  tools.value.slice().sort((a, b) => a.name.localeCompare(b.name))
);

onMounted(() => {
  store.dispatch('aiAgentTools/get');
});

const openEdit = tool => {
  editedTool.value = tool;
  editedDescription.value = tool.description_for_llm || '';
  editedReadOnly.value = !!tool.read_only;
  editDialogRef.value?.open();
};

const submitEdit = async ({ changeConfirmed = false } = {}) => {
  try {
    await store.dispatch('aiAgentTools/update', {
      id: editedTool.value.id,
      description_for_llm: editedDescription.value,
      read_only: editedReadOnly.value,
      changeConfirmed,
    });
    useAlert(t('AI_AGENTS.TOOLS.EDIT.SUCCESS'));
    editDialogRef.value?.close();
    dependentsDialogRef.value?.close();
  } catch (error) {
    if (error.response?.status === 409) {
      dependentAgents.value = error.response.data?.dependent_agents || [];
      dependentsDialogRef.value?.open();
    } else {
      useAlert(t('AI_AGENTS.TOOLS.EDIT.ERROR'));
    }
  }
};

const dependentAgentNames = computed(() =>
  dependentAgents.value.map(agent => agent.display_name).join(', ')
);
</script>

<template>
  <AgentLayout v-if="brain.id" :brain="brain">
    <div class="flex flex-col gap-4">
      <div>
        <h2 class="text-lg font-medium text-n-slate-12">
          {{ t('AI_AGENTS.TOOLS.HEADER') }}
        </h2>
        <p class="text-sm text-n-slate-11">
          {{ t('AI_AGENTS.TOOLS.DESCRIPTION') }}
        </p>
      </div>

      <p v-if="!sortedTools.length" class="text-sm text-n-slate-11">
        {{ t('AI_AGENTS.TOOLS.EMPTY') }}
      </p>

      <table v-else class="w-full text-sm text-left">
        <thead>
          <tr class="text-xs uppercase tracking-wide text-n-slate-10">
            <th class="px-2 py-2">{{ t('AI_AGENTS.TOOLS.COLUMNS.NAME') }}</th>
            <th class="px-2 py-2">{{ t('AI_AGENTS.TOOLS.COLUMNS.KIND') }}</th>
            <th class="px-2 py-2">
              {{ t('AI_AGENTS.TOOLS.COLUMNS.CONNECTOR') }}
            </th>
            <th class="px-2 py-2">
              {{ t('AI_AGENTS.TOOLS.COLUMNS.READ_ONLY') }}
            </th>
            <th class="px-2 py-2">
              {{ t('AI_AGENTS.TOOLS.COLUMNS.DEPENDENTS') }}
            </th>
            <th class="px-2 py-2" />
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="tool in sortedTools"
            :key="tool.id"
            class="border-t border-n-weak text-n-slate-12"
          >
            <td class="px-2 py-2 font-medium">{{ tool.name }}</td>
            <td class="px-2 py-2">
              {{ t(`AI_AGENTS.TOOLS.KIND.${tool.kind.toUpperCase()}`) }}
            </td>
            <td class="px-2 py-2">{{ tool.connector_name }}</td>
            <td class="px-2 py-2">
              {{
                tool.read_only
                  ? t('AI_AGENTS.TOOLS.YES')
                  : t('AI_AGENTS.TOOLS.NO')
              }}
            </td>
            <td class="px-2 py-2">{{ tool.dependent_agent_count }}</td>
            <td class="px-2 py-2 text-right">
              <Button
                v-if="isAdmin"
                :label="t('AI_AGENTS.TOOLS.EDIT.BUTTON')"
                variant="ghost"
                size="sm"
                @click="openEdit(tool)"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <Dialog
      ref="editDialogRef"
      :title="t('AI_AGENTS.TOOLS.EDIT.TITLE')"
      :confirm-button-label="t('AI_AGENTS.TOOLS.EDIT.SAVE')"
      :cancel-button-label="t('AI_AGENTS.COMMON.CANCEL')"
      :is-loading="uiFlags.updatingItem"
      @confirm="submitEdit()"
    >
      <div class="flex flex-col gap-3">
        <label class="flex flex-col gap-1 text-sm text-n-slate-12">
          {{ t('AI_AGENTS.TOOLS.EDIT.DESCRIPTION_LABEL') }}
          <textarea
            v-model="editedDescription"
            rows="4"
            class="px-3 py-2 text-sm rounded-lg outline outline-1 outline-n-weak bg-n-solid-1 text-n-slate-12"
          />
        </label>
        <label class="flex items-center gap-2 text-sm text-n-slate-12">
          <input v-model="editedReadOnly" type="checkbox" />
          {{ t('AI_AGENTS.TOOLS.EDIT.READ_ONLY_LABEL') }}
        </label>
      </div>
    </Dialog>

    <Dialog
      ref="dependentsDialogRef"
      type="alert"
      :title="t('AI_AGENTS.TOOLS.DEPENDENTS_CONFIRM.TITLE')"
      :description="
        t('AI_AGENTS.TOOLS.DEPENDENTS_CONFIRM.MESSAGE', {
          agents: dependentAgentNames,
        })
      "
      :confirm-button-label="t('AI_AGENTS.TOOLS.DEPENDENTS_CONFIRM.CONFIRM')"
      :cancel-button-label="t('AI_AGENTS.TOOLS.DEPENDENTS_CONFIRM.CANCEL')"
      :is-loading="uiFlags.updatingItem"
      @confirm="submitEdit({ changeConfirmed: true })"
    />
  </AgentLayout>
  <div
    v-else
    class="flex items-center justify-center w-full h-full text-n-slate-11"
  >
    <Spinner />
  </div>
</template>
